/**
 * Bounding helpers for the messaging session stores.
 *
 * A messaging session is long-lived by design — an operator keeps the
 * workspace open for a whole shift — so every "session-lived" store here is
 * really "shift-lived", and anything keyed per message or per conversation
 * grows with volume rather than with anything the operator can see. These two
 * helpers are the shared way to stop that, matching `insightDismissStore`,
 * which has capped itself at 200 from the start.
 */

/**
 * `map.set`, evicting the OLDEST entries once `max` is exceeded.
 *
 * FIFO is the right policy for both callers because both are guards over
 * RECENT activity: a delivery-state patch arrives within seconds of its send,
 * and a retry targets a message the operator can still see. The oldest entry
 * is therefore the one least likely to be consulted again, and both callers
 * degrade gracefully when a guard is missing rather than misbehaving — see
 * their own comments.
 *
 * Relies on Map's guaranteed insertion-order iteration.
 */
export function setBounded<K, V>(map: Map<K, V>, key: K, value: V, max: number): void {
  map.set(key, value)
  while (map.size > max) {
    const oldest = map.keys().next()
    if (oldest.done) {
      return
    }

    map.delete(oldest.value)
  }
}

/**
 * `localStorage.setItem` that survives a full quota.
 *
 * The messaging keys are one-per-conversation and were never pruned, so the
 * origin's quota is a slow ceiling shared with every other feature. Hitting
 * it used to be swallowed in silence, which is the part that actually hurt:
 * `clientSeq` stops persisting, send ordering quietly degrades across
 * reloads, and nothing anywhere says so.
 *
 * On failure this prunes the caller's own key family and retries once. Eviction
 * order is arbitrary — localStorage keys carry no timestamp — but that is
 * acceptable HERE because it only runs under real quota pressure, and both key
 * families are designed to survive losing an entry: a dropped `clientSeq`
 * re-seeds from the server `floor` on the next send (the documented cold-start
 * path), and a dropped `lastReadSeq` only re-marks one conversation unread.
 *
 * The key being written is excluded from the prune, and that is what keeps
 * eviction CORRECTNESS-safe rather than merely size-safe: both families are
 * written only for the conversation currently on screen (`nextClientSeq` from
 * the composer, `markConversationRead` from the open thread), so "the key
 * being written" and "the open conversation" are the same key. Eviction
 * therefore always falls on a conversation the operator is not looking at —
 * it can never reorder a send in the thread they are typing in, nor resurface
 * as unread the thread they are reading.
 *
 * Returns whether the value is now persisted, so the caller can decide what a
 * failure means instead of assuming it cannot happen.
 */
export function setItemPruning(
  key: string,
  value: string,
  prefix: string,
  keepAtMost: number,
): boolean {
  try {
    localStorage.setItem(key, value)
    return true
  } catch {
    // Fall through to prune + retry.
  }

  try {
    const owned: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const candidate = localStorage.key(i)
      if (candidate?.startsWith(prefix) && candidate !== key) {
        owned.push(candidate)
      }
    }

    for (const doomed of owned.slice(0, Math.max(0, owned.length - keepAtMost))) {
      localStorage.removeItem(doomed)
    }

    localStorage.setItem(key, value)
    return true
  } catch {
    // Genuinely out of room, or storage is unavailable entirely (private
    // mode, blocked cookies). The caller reports it; we do not pretend.
    return false
  }
}
